// Phase A — Notary Act Deep Content
// Data-driven enrichment applied to all 32 acts × TH/EN via NotaryActDeepContent.
// Content is contextual (per act) with sensible fallbacks derived from NotaryAct.

import type { NotaryAct } from "@/data/notary-acts";

export interface ActMistake { th: string; en: string }
export interface ActCase { th: string; en: string }
export interface DestNote {
  code: string;
  flag: string;
  nameTh: string;
  nameEn: string;
  chain: "apostille" | "embassy";
  noteTh: string;
  noteEn: string;
}

/** Top-15 destination countries with Hague vs Embassy chain notes (Dec 2025+). */
export const TOP_NOTARY_DESTINATIONS: DestNote[] = [
  { code: "us", flag: "🇺🇸", nameTh: "สหรัฐอเมริกา", nameEn: "United States", chain: "apostille",
    noteTh: "ตั้งแต่ 15 ธ.ค. 2025 ไทยเข้า Hague — ยื่น MFA Apostille แล้วส่งตรง USCIS/State ได้เลย ไม่ต้องผ่านสถานทูตสหรัฐ",
    noteEn: "From 15 Dec 2025, Thailand joins the Apostille Convention. Notarise → MFA Apostille → send directly to USCIS/State (US embassy no longer required)." },
  { code: "uk", flag: "🇬🇧", nameTh: "สหราชอาณาจักร", nameEn: "United Kingdom", chain: "apostille",
    noteTh: "UK ยอมรับ MFA Apostille ของไทยแทน FCDO Legalisation — เร็วขึ้น 7–10 วัน",
    noteEn: "UK Home Office and FCDO accept Thai MFA Apostille — 7–10 days faster than legacy consular chain." },
  { code: "au", flag: "🇦🇺", nameTh: "ออสเตรเลีย", nameEn: "Australia", chain: "apostille",
    noteTh: "DHA และ VEVO ยอมรับ Apostille — คู่กับคำแปล NAATI ใช้ยื่นวีซ่า 189/190/491/500/820 ได้ตรง",
    noteEn: "DHA/Home Affairs accepts Thai Apostille. Pair with NAATI translation for visa subclasses 189, 190, 491, 500, 820, 186." },
  { code: "de", flag: "🇩🇪", nameTh: "เยอรมนี", nameEn: "Germany", chain: "apostille",
    noteTh: "สถานทูตเยอรมันในกรุงเทพยอมรับ Apostille + คำแปล beeidigte Übersetzer",
    noteEn: "German Embassy Bangkok accepts Apostille with sworn (beeidigte) German translation." },
  { code: "fr", flag: "🇫🇷", nameTh: "ฝรั่งเศส", nameEn: "France", chain: "apostille",
    noteTh: "ฝรั่งเศส Apostille ตรง — คู่กับ traducteur assermenté",
    noteEn: "France requires Apostille + traducteur assermenté (sworn French translator)." },
  { code: "jp", flag: "🇯🇵", nameTh: "ญี่ปุ่น", nameEn: "Japan", chain: "apostille",
    noteTh: "MOFA JP รับ Apostille — คู่กับคำแปลญี่ปุ่นและตราประทับ hanko หากเป็นสัญญาธุรกิจ",
    noteEn: "Japan MOFA accepts Apostille. Business contracts should pair with Japanese translation and hanko where relevant." },
  { code: "kr", flag: "🇰🇷", nameTh: "เกาหลีใต้", nameEn: "South Korea", chain: "apostille",
    noteTh: "เกาหลีใต้เป็นสมาชิก Hague — คู่กับคำแปลเกาหลีรับรอง สำหรับ F-6 spouse visa, D-2 study, E-7 work",
    noteEn: "Korea (Hague member) accepts Apostille. Pair with certified Korean translation for F-6, D-2, E-7 visas." },
  { code: "cn", flag: "🇨🇳", nameTh: "จีน (แผ่นดินใหญ่)", nameEn: "China (PRC)", chain: "apostille",
    noteTh: "จีน Apostille ใช้ได้แล้ว (Nov 2023) — สถานทูตจีนในไทยเลิกรับ Legalization รูปแบบเดิม",
    noteEn: "China accepts Apostille since Nov 2023. Chinese Embassy in Thailand no longer issues legacy legalization." },
  { code: "sg", flag: "🇸🇬", nameTh: "สิงคโปร์", nameEn: "Singapore", chain: "apostille",
    noteTh: "SAL (Singapore Academy of Law) และ ICA ยอมรับ Apostille — ใช้กับ Employment Pass, PR application",
    noteEn: "SAL and ICA Singapore accept Apostille — used for EP, S Pass, PR applications." },
  { code: "hk", flag: "🇭🇰", nameTh: "ฮ่องกง", nameEn: "Hong Kong", chain: "apostille",
    noteTh: "HK High Court รับ Apostille (Hague member) — ธุรกรรมและวีซ่าใช้ได้เลย",
    noteEn: "Hong Kong High Court accepts Apostille (Hague member) — usable for corporate filings and visas." },
  { code: "tw", flag: "🇹🇼", nameTh: "ไต้หวัน", nameEn: "Taiwan", chain: "embassy",
    noteTh: "ไต้หวันไม่ใช่สมาชิก Hague — ต้องผ่าน MFA แล้วรับรองที่ TECO Bangkok เท่านั้น",
    noteEn: "Taiwan is NOT a Hague member. Notarise → MFA → legalize at TECO Bangkok (embassy chain still required)." },
  { code: "in", flag: "🇮🇳", nameTh: "อินเดีย", nameEn: "India", chain: "apostille",
    noteTh: "อินเดียเป็นสมาชิก Hague — Apostille ใช้ได้ตรง เหมาะกับ PAN Card, OCI, business visa",
    noteEn: "India (Hague member) accepts Apostille — ideal for PAN Card, OCI, business visa filings." },
  { code: "ae", flag: "🇦🇪", nameTh: "สหรัฐอาหรับเอมิเรตส์", nameEn: "UAE", chain: "embassy",
    noteTh: "UAE ยังไม่รับ Apostille ไทย — ต้อง MFA + UAE Embassy Bangkok (2,600 บาท/ชุด) + MOFAIC Dubai",
    noteEn: "UAE has not yet accepted Thai Apostille. Chain: notary → MFA → UAE Embassy Bangkok (฿2,600/doc) → MOFAIC Dubai." },
  { code: "sa", flag: "🇸🇦", nameTh: "ซาอุดีอาระเบีย", nameEn: "Saudi Arabia", chain: "embassy",
    noteTh: "ซาอุฯ ไม่ใช่ Hague — ต้อง MFA + Saudi Embassy Bangkok + คำแปลอารบิกโดยนักแปลสาบาน",
    noteEn: "Saudi Arabia (non-Hague). Chain: notary → MFA → Saudi Embassy Bangkok + certified Arabic sworn translation." },
  { code: "ru", flag: "🇷🇺", nameTh: "รัสเซีย", nameEn: "Russia", chain: "apostille",
    noteTh: "รัสเซียยอมรับ Apostille ไทย — คู่กับคำแปลรัสเซียโดยนักแปลรับรอง (нотариально заверенный)",
    noteEn: "Russia accepts Thai Apostille. Pair with notarised Russian translation (нотариально заверенный перевод)." },
  { code: "pk", flag: "🇵🇰", nameTh: "ปากีสถาน", nameEn: "Pakistan", chain: "embassy",
    noteTh: "ปากีสถานยังไม่ใช่ Hague — Notary → MFA → สถานทูตปากีสถาน กรุงเทพ + คำแปลอูรดู/อังกฤษ",
    noteEn: "Pakistan (non-Hague). Chain: notary → MFA → Pakistan Embassy Bangkok + Urdu/English translation." },
  { code: "co", flag: "🇨🇴", nameTh: "โคลอมเบีย", nameEn: "Colombia", chain: "apostille",
    noteTh: "โคลอมเบียเป็นสมาชิก Hague — Apostille + คำแปลสเปน traducción oficial",
    noteEn: "Colombia (Hague). Apostille + Spanish sworn translation (traducción oficial)." },
  { code: "uy", flag: "🇺🇾", nameTh: "อุรุกวัย", nameEn: "Uruguay", chain: "apostille",
    noteTh: "อุรุกวัย Hague — Apostille + สเปน traductor público",
    noteEn: "Uruguay (Hague). Apostille + Spanish sworn translator (traductor público)." },
  { code: "ng", flag: "🇳🇬", nameTh: "ไนจีเรีย", nameEn: "Nigeria", chain: "embassy",
    noteTh: "ไนจีเรียยังไม่ใช่ Hague — Notary → MFA → Nigeria High Commission Bangkok",
    noteEn: "Nigeria (non-Hague). Chain: notary → MFA → Nigeria High Commission Bangkok." },
  { code: "ke", flag: "🇰🇪", nameTh: "เคนยา", nameEn: "Kenya", chain: "embassy",
    noteTh: "เคนยา non-Hague — ต้องผ่าน MFA + Kenya Embassy Bangkok",
    noteEn: "Kenya (non-Hague). MFA + Kenya Embassy Bangkok required." },
  { code: "lk", flag: "🇱🇰", nameTh: "ศรีลังกา", nameEn: "Sri Lanka", chain: "embassy",
    noteTh: "ศรีลังกา non-Hague — MFA + สถานทูตศรีลังกา กรุงเทพ",
    noteEn: "Sri Lanka (non-Hague). MFA + Sri Lanka Embassy Bangkok." },
  { code: "bd", flag: "🇧🇩", nameTh: "บังกลาเทศ", nameEn: "Bangladesh", chain: "embassy",
    noteTh: "บังกลาเทศ non-Hague — MFA + Bangladesh High Commission",
    noteEn: "Bangladesh (non-Hague). MFA + Bangladesh High Commission." },
  { code: "ir", flag: "🇮🇷", nameTh: "อิหร่าน", nameEn: "Iran", chain: "embassy",
    noteTh: "อิหร่าน non-Hague — MFA + Iranian Embassy Bangkok + คำแปลเปอร์เซีย",
    noteEn: "Iran (non-Hague). MFA + Iran Embassy Bangkok + Persian translation." },
  { code: "eg", flag: "🇪🇬", nameTh: "อียิปต์", nameEn: "Egypt", chain: "embassy",
    noteTh: "อียิปต์ non-Hague — MFA + Egyptian Embassy + คำแปลอารบิก",
    noteEn: "Egypt (non-Hague). MFA + Egyptian Embassy + Arabic translation." },
  { code: "qa", flag: "🇶🇦", nameTh: "กาตาร์", nameEn: "Qatar", chain: "embassy",
    noteTh: "กาตาร์ non-Hague — MFA + Qatar Embassy + MOFA Doha ปลายทาง",
    noteEn: "Qatar (non-Hague). MFA + Qatar Embassy + MOFA Doha at destination." },
  { code: "la", flag: "🇱🇦", nameTh: "ลาว", nameEn: "Laos", chain: "embassy",
    noteTh: "ลาว non-Hague — MFA + สถานทูตลาว กรุงเทพ (ค่าธรรมเนียมต่ำ, 2-3 วัน)",
    noteEn: "Laos (non-Hague). MFA + Lao Embassy Bangkok (low fee, 2-3 days)." },
  { code: "kh", flag: "🇰🇭", nameTh: "กัมพูชา", nameEn: "Cambodia", chain: "embassy",
    noteTh: "กัมพูชา non-Hague — MFA + สถานทูตกัมพูชา กรุงเทพ + คำแปลเขมร",
    noteEn: "Cambodia (non-Hague). MFA + Cambodian Embassy Bangkok + Khmer translation." },
  { code: "mm", flag: "🇲🇲", nameTh: "เมียนมา", nameEn: "Myanmar", chain: "embassy",
    noteTh: "เมียนมา non-Hague — MFA + สถานทูตเมียนมา + คำแปลพม่า/อังกฤษ",
    noteEn: "Myanmar (non-Hague). MFA + Myanmar Embassy + Burmese/English translation." },
  { code: "id", flag: "🇮🇩", nameTh: "อินโดนีเซีย", nameEn: "Indonesia", chain: "apostille",
    noteTh: "อินโดนีเซีย Hague (2022) — Apostille + Bahasa Indonesia sworn translator",
    noteEn: "Indonesia (Hague since 2022). Apostille + Bahasa Indonesia sworn translation." },
];

/** Common mistakes — mostly generic, with per-slug overrides. */
const GENERIC_MISTAKES: ActMistake[] = [
  { th: "ส่งสำเนาแทนต้นฉบับ — ทนายรับรองต้องเห็นเอกสารตัวจริงเท่านั้น",
    en: "Submitting a copy instead of the original — the notary must physically inspect the original." },
  { th: "เซ็นชื่อล่วงหน้า — ต้องเซ็นต่อหน้าทนายเท่านั้นตามระเบียบสภาทนายความ",
    en: "Pre-signing the document — Lawyers Council rules require signing in the notary's physical presence." },
  { th: "เอาแค่ Notary ไปยื่น — ประเทศส่วนใหญ่ต้องมี MFA Apostille หรือ Embassy Legalization ต่อ",
    en: "Submitting notary-only certificates — most destinations require MFA Apostille or embassy legalization." },
  { th: "ใช้ Passport หมดอายุ / บัตรประชาชนหมดอายุ — Notary จะไม่รับรองให้",
    en: "Bringing an expired passport or Thai ID — notaries cannot verify identity from expired documents." },
];

const MISTAKE_OVERRIDES: Record<string, ActMistake[]> = {
  "affidavit-single": [
    { th: "ใช้หนังสือรับรองโสด (สด.1/สด.2) เก่าเกิน 3 เดือน — หลายสถานทูตไม่รับ",
      en: "Using a Thai single-status certificate older than 3 months — most embassies reject it." },
    { th: "แปลผิดสำนวน 'never married' vs 'single' — บางประเทศตีความต่างกัน (เช่น เยอรมนี)",
      en: "Confusing 'never married' with 'single' — some jurisdictions (e.g. Germany) treat them differently." },
    ...GENERIC_MISTAKES.slice(0, 2),
  ],
  "power-of-attorney": [
    { th: "POA ไม่ระบุขอบเขตอำนาจชัดเจน — สถานทูต/ธนาคารต่างประเทศจะไม่รับ",
      en: "POA lacking specific scope — foreign banks and embassies routinely reject vague authorities." },
    { th: "ลืมระบุวันหมดอายุ POA — บางประเทศตีความว่าไม่สมบูรณ์",
      en: "Omitting an expiry clause — some jurisdictions treat undated POAs as invalid." },
    ...GENERIC_MISTAKES.slice(0, 2),
  ],
  "dbd-signature": [
    { th: "หนังสือรับรองบริษัท DBD เก่าเกิน 30 วัน — DBD ต้องการฉบับใหม่ล่าสุด",
      en: "Company affidavit older than 30 days — DBD demands the latest issue." },
    ...GENERIC_MISTAKES.slice(0, 3),
  ],
  "certified-true-copy": [
    { th: "ให้ทนายรับรองสำเนา 'เก่า' ที่ถ่ายไว้ล่วงหน้า — ต้องถ่ายและรับรองในวันเดียว",
      en: "Presenting old photocopies — the notary must copy the original and certify same day." },
    ...GENERIC_MISTAKES.slice(0, 3),
  ],
  "life-certificate": [
    { th: "ใช้รูปถ่ายเก่าเกิน 30 วัน — SSA/DWP บางประเทศตีความว่า Proof of Life ไม่ทันสมัย",
      en: "Photo older than 30 days — SSA/DWP may treat as not current Proof of Life." },
    { th: "ไม่แนบสำเนาพาสปอร์ตหน้าตราประทับล่าสุด — สถานทูตต้องการเพื่อยืนยันตัวตน",
      en: "Missing latest passport stamp copy — required for identity confirmation." },
    ...GENERIC_MISTAKES.slice(0, 2),
  ],
  "id-verification": [
    { th: "ใช้ใบขับขี่แทนพาสปอร์ตต่อหน้าทนายชาวต่างชาติ — ต้องใช้พาสปอร์ตเสมอ",
      en: "Using driving licence instead of passport for foreign nationals — passport required." },
    ...GENERIC_MISTAKES.slice(0, 3),
  ],
  "passport-copy": [
    { th: "รับรองสำเนาหน้าที่มีตราวีซ่าเก่าหมดอายุ — บางสถานทูตต้องการทั้งเล่ม",
      en: "Certifying only the bio page when embassy needs full passport including expired visas." },
    ...GENERIC_MISTAKES.slice(0, 3),
  ],
};


export function mistakesFor(act: NotaryAct): ActMistake[] {
  return MISTAKE_OVERRIDES[act.slug] ?? GENERIC_MISTAKES;
}

/** Micro-case study (anonymised) — per-slug where meaningful, generic otherwise. */
const CASE_OVERRIDES: Record<string, ActCase> = {
  "affidavit-single": {
    th: "เคส K.M. (มี.ค. 2026): ลูกค้าไทยจดทะเบียนสมรสที่ Standesamt เยอรมนี — NYC ออก Affidavit of Single Status + Apostille + คำแปลเยอรมันโดย beeidigte Übersetzerin ครบใน 4 วันทำการ ค่าใช้จ่ายรวม ฿5,800",
    en: "Case K.M. (Mar 2026): Thai client marrying at a German Standesamt. NYC produced the Affidavit of Single Status + MFA Apostille + sworn German translation in 4 business days, total ฿5,800.",
  },
  "power-of-attorney": {
    th: "เคส R.T. (ก.พ. 2026): มอบอำนาจให้ทนายที่ลอนดอนขาย flat ในย่าน Kensington — POA เฉพาะกิจ + Apostille + คำแปลอังกฤษ certified ส่ง DHL ถึงลอนดอนภายใน 5 วัน",
    en: "Case R.T. (Feb 2026): Client authorised a London solicitor to sell a Kensington flat. Specific POA + Apostille + certified English translation, delivered to London via DHL in 5 days.",
  },
  "life-certificate": {
    th: "เคส Mr. B. (ม.ค. 2026): ผู้เกษียณอายุอเมริกันในเชียงใหม่ ต้องส่ง Proof of Life ให้ Social Security Administration ทุกปี — NYC ออก Life Certificate + Apostille ครบใน 2 วัน ค่าใช้จ่าย ฿3,700",
    en: "Case Mr. B. (Jan 2026): US retiree in Chiang Mai needed annual Proof of Life for SSA. NYC issued the Life Certificate + Apostille in 2 days, total ฿3,700.",
  },
  "dbd-signature": {
    th: "เคส บริษัท S. Ltd. (พ.ย. 2025): เปลี่ยนกรรมการ 3 ท่านพร้อมกัน — NYC จัดทีมทนายเดินทางไปสำนักงานลูกค้าที่ One Bangkok รับรอง DBD signature 12 ฉบับในวันเดียว",
    en: "Case S. Ltd. (Nov 2025): Simultaneous change of 3 directors. NYC dispatched a notary to the client's One Bangkok office and certified 12 DBD signatures in a single visit.",
  },
};

export function caseStudyFor(act: NotaryAct): ActCase {
  return (
    CASE_OVERRIDES[act.slug] ?? {
      th: `เคสตัวอย่าง: ลูกค้าใช้บริการ ${act.nameTh} เพื่อ ${act.useCases[0] ?? "ใช้ต่างประเทศ"} — เสร็จภายใน ${act.turnaround} รวมค่าใช้จ่ายประมาณ ฿${(act.priceFromTh + 800).toLocaleString()} (Notary + MFA Apostille)`,
      en: `Sample case: A client used ${act.nameEn} to support ${act.useCases[0] ?? "an overseas requirement"}. Completed in ${act.turnaround}; total cost around ฿${(act.priceFromTh + 800).toLocaleString()} including MFA Apostille.`,
    }
  );
}

/** Regulatory footnote (Dec 2025+ Hague accession). Uniform across acts. */
export const REGULATORY_2025 = {
  th: "อัปเดต 15 ธ.ค. 2025 — ประเทศไทยเข้าร่วม Hague Apostille Convention อย่างเป็นทางการ กระทรวงการต่างประเทศ (MFA) แจ้งวัฒนะออก Apostille ให้เอกสาร Notary ได้โดยตรง 30+ ประเทศสมาชิก Hague เลิกใช้ Embassy Legalization รูปแบบเดิม (ประเทศนอก Hague เช่น UAE, ซาอุฯ, ไต้หวัน ยังต้องผ่านสถานทูต) ค่าธรรมเนียม Apostille ราชการ 200 บาท/ชุด ระยะเวลารับ 2-3 วันทำการ อ้างอิง: กระทรวงการต่างประเทศ • Ministerial Regulation B.E. 2568",
  en: "Regulatory update — 15 Dec 2025: Thailand's accession to the Hague Apostille Convention took effect. The Thai Ministry of Foreign Affairs (Chaeng Watthana) now issues an Apostille directly on notarised documents for 30+ Hague member states, replacing the prior consular legalization chain. Non-Hague destinations (UAE, Saudi Arabia, Taiwan) still require embassy legalization. Government fee ฿200/doc, 2–3 business days. Source: Thailand MFA & Ministerial Regulation B.E. 2568.",
};

/** Cross-service bridges — every act links to complementary services. */
export interface Bridge { toTh: string; toEn: string; hrefTh: string; hrefEn: string; whyTh: string; whyEn: string }
export const CROSS_BRIDGES: Bridge[] = [
  { toTh: "รับรองเอกสาร MFA / กงสุลไทย", toEn: "MFA / Thai consular legalization",
    hrefTh: "/mfa-legalisation", hrefEn: "/en/mfa-legalisation",
    whyTh: "หลัง Notary ต่อด้วย MFA Apostille (Hague) หรือ Legalization (นอก Hague) เพื่อให้ใช้ต่างประเทศได้",
    whyEn: "After notarisation, add MFA Apostille (Hague) or Legalization (non-Hague) so the document is usable abroad." },
  { toTh: "รับรองโดยสถานทูตปลายทาง", toEn: "Destination embassy legalization",
    hrefTh: "/embassy", hrefEn: "/en/embassy",
    whyTh: "ประเทศนอก Hague เช่น UAE, ซาอุฯ, ไต้หวัน ยังต้องผ่านสถานทูตเพิ่ม",
    whyEn: "Non-Hague destinations still need embassy legalization on top of MFA." },
  { toTh: "แปลและรับรองโดยนักแปลรับรอง", toEn: "Certified / sworn translation",
    hrefTh: "/sworn-translator", hrefEn: "/en/sworn-translator",
    whyTh: "เอกสารส่วนใหญ่ต้องแปลเป็นภาษาปลายทางโดยนักแปลรับรองก่อน Notary",
    whyEn: "Most documents must be translated by a sworn translator into the destination language before notarisation." },
  { toTh: "แปลและรับรอง NAATI (ออสเตรเลีย)", toEn: "NAATI translation (Australia)",
    hrefTh: "/translate/naati", hrefEn: "/en/translate/naati",
    whyTh: "หากส่งเอกสารไปออสเตรเลีย DHA ต้องการคำแปล NAATI-certified คู่กับ Notary/Apostille",
    whyEn: "For Australia, DHA requires NAATI-certified translation paired with the notarised original + Apostille." },
];
